import { Button } from '@/components/primitives/Button';
import { Typography } from '@/components/primitives/Typography';
import PassengerInfo from '@/components/purchase/checkout/PassengerInfo';
import { type PersonDataField } from '@/hooks/usePersonFieldRenderer';
import { TransText } from '@/i18n/trans/text';
import type { CheckoutPassenger } from '@/types/passenger';
import { getPassengerName } from '@/utils/passenger';
import type { CheckoutValues } from '@/utils/zodSchema';
import { type FC, useCallback, useState } from 'react';
import { useFormContext } from 'react-hook-form';

interface PurchaseProps {
  formFields: Array<PersonDataField>;
  passengerFields: Array<PersonDataField>;
}

const Purchaser: FC<PurchaseProps> = ({ formFields, passengerFields }) => {
  const [showLoader, setShowLoader] = useState(false);
  const { watch, setValue } = useFormContext<CheckoutValues>();
  const passengers = watch('passengers');

  const isDisabled = useCallback(
    (passenger: CheckoutPassenger) => {
      if (!passenger) return true;

      for (const field of passengerFields) {
        if (passenger[field.name]) {
          return false;
        }
      }

      return true;
    },
    [passengerFields]
  );

  return (
    <div className="flex flex-col gap-3 tablet:flex-row tablet:gap-6">
      <div className="flex flex-col gap-3 tablet:flex-1 tablet:gap-4">
        <Typography variant="body2" className="text-neutral">
          <TransText i18nKey="fillWithDataFrom" />
          {':'}
        </Typography>
        <div className="flex flex-col gap-2">
          {passengers.map((passenger, index) => (
            <Button
              key={index}
              size="small"
              variant="secondary"
              disabled={isDisabled(passenger)}
              className="grid"
              onClick={() => {
                setShowLoader(true);
                setValue('purchaser', passenger);
                setTimeout(() => setShowLoader(false), 500);
              }}
              data-testid="purchaser-passenger-button"
            >
              {showLoader ? (
                <div className="h-4 w-4 animate-spin rounded-full border-t-4 border-primary" />
              ) : (
                <Typography variant="body2-bold" className="truncate">
                  {getPassengerName(passenger, index)}
                </Typography>
              )}
            </Button>
          ))}
        </div>
      </div>
      <PassengerInfo
        formFields={formFields}
        prefix="purchaser"
        data-testid="purchaser-content"
      />
    </div>
  );
};

export default Purchaser;
